import React, { useCallback, useState, useEffect } from 'react';
import { AppBar, Avatar, Box, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemText } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { deleteAuthSession, getBillingStatus, getUserProfile, getUserScore, getAuthStatus } from '../utils/api';
import { unregisterCurrentPushDevice } from '../utils/firebaseNotifications';
import ArcadeScoreboard from './ArcadeScoreboard';

import AIUsageCredits from './AIUsageCredits.js';
import FriendsLeaderboard from './FriendsLeaderboard';
import { LeaderboardIcon } from './PixelIcons';
const PUSH_CLEANUP_TIMEOUT_MS = 2000;

const waitForPushCleanup = async () => {
  let timeoutId;
  try {
    await Promise.race([
      unregisterCurrentPushDevice(),
      new Promise((resolve) => {
        timeoutId = window.setTimeout(resolve, PUSH_CLEANUP_TIMEOUT_MS);
      }),
    ]);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const NavBar = ({ onOpenAssistant }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [scores, setScores] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [canManageTutorials, setCanManageTutorials] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const openAssistant = onOpenAssistant || (() => navigate('/assistant'));
  const [billingStatus, setBillingStatus] = useState(null);

  const fetchProfile = useCallback(async () => {
    const [profileResult, billingResult, scoreResult] = await Promise.allSettled([
      getUserProfile(),
      getBillingStatus(),
      getUserScore(),
    ]);
    if (profileResult.status === 'fulfilled') {
      setProfile(profileResult.value);
    } else {
      console.error('Error fetching profile:', profileResult.reason);
    }
    if (scoreResult.status === 'fulfilled') {
      setScores(scoreResult.value);
    } else {
      setScores(null);
      console.error('Error fetching scores:', scoreResult.reason);
    }
    if (billingResult.status === 'fulfilled') {
      setBillingStatus(billingResult.value || null);
    } else {
      console.error('Error fetching AI usage:', billingResult.reason);
      setBillingStatus(null);
    }
  }, []);

  const syncAuthAndProfile = useCallback(async () => {
    try {
      const auth = await getAuthStatus();
      const authenticated = !!auth?.authenticated;
      setIsAuthenticated(authenticated);
      setCanManageTutorials(auth?.capabilities?.tutorial_builder === true);

      if (authenticated) {
        await fetchProfile();
      } else {
        setProfile(null);
        setScores(null);
        setBillingStatus(null);
        setCanManageTutorials(false);
      }
    } catch (err) {
      console.error('Error checking auth:', err);
      setIsAuthenticated(false);
      setCanManageTutorials(false);
      setProfile(null);
      setScores(null);
      setBillingStatus(null);
    }
  }, [fetchProfile]);

  useEffect(() => {
    syncAuthAndProfile();
  }, [location.pathname, syncAuthAndProfile]);

  useEffect(() => {
    const handleProfileUpdate = () => {
      syncAuthAndProfile();
    };
    window.addEventListener('profileUpdate', handleProfileUpdate);
    return () => {
      window.removeEventListener('profileUpdate', handleProfileUpdate);
    };
  }, [syncAuthAndProfile]);

  useEffect(() => {
    const handleBillingStatusUpdate = () => {
      if (isAuthenticated) fetchProfile();
    };
    window.addEventListener('billingStatusUpdate', handleBillingStatusUpdate);
    return () => {
      window.removeEventListener('billingStatusUpdate', handleBillingStatusUpdate);
    };
  }, [fetchProfile, isAuthenticated]);
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const refresh = () => { if (document.visibilityState === 'visible') fetchProfile(); };
    const endsAt = Date.parse(scores?.day_ends_at);
    const timer = Number.isFinite(endsAt)
      ? window.setTimeout(fetchProfile, Math.max(1000, endsAt - Date.now() + 100))
      : null;
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [isAuthenticated, scores?.day_ends_at, fetchProfile]);

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

const navItems = isAuthenticated
  ? [
      { text: 'Home', to: '/user-home' },
      { text: 'Assistant', action: 'assistant' },
      { text: 'Profile', to: '/profile', showAvatar: true },
      { text: 'Feedback', to: '/feedback' },
      { text: 'Friends', to: '/friends' },
      { text: 'Gallery', to: '/gallery' },
      ...(canManageTutorials ? [{ text: 'Tutorial Builder', to: '/tutorial-builder' }] : []),
      { text: 'Sign Out', action: 'logout' },
      { text: 'About', to: '/about' },
    ]
  : [
      { text: 'Home', to: '/' },
      { text: 'About', to: '/about' },
      { text: 'Sign In', to: '/sign-in' },
    ];
const handleLogout = async () => {
  try {
    await waitForPushCleanup();
  } catch (err) {
    console.error('Unable to remove push registration during logout', err);
  }
  try {
    await deleteAuthSession();
    setIsAuthenticated(false);
    setCanManageTutorials(false);
    setProfile(null);
    setBillingStatus(null);
    window.location.href = '/'; // Redirect to sign-in after logout
  } catch (err) {
    console.error('Error during logout', err);
  }
};
  return (
    <Box id="app-top-navigation" sx={{ width: '100%', minWidth: 0, px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 2 } }} mb={2}>
      <AppBar
        id="app-top-navigation-bar"
        position="static"
        sx={{
          backgroundColor: '#0A0F0D',
          border: '4px solid #39FF14',
          borderRadius: '20px',
          width: '100%',
        }}
        elevation={0}
      >
        <Toolbar sx={{ width: '100%', gap: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, minWidth: 0, overflowWrap: 'anywhere' }}
          >
            Dopatize
          </Typography>
          <IconButton
            id="app-top-navigation-menu"
            edge="end"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ flexShrink: 0 }}

          >
            <MenuIcon color="primary" />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          id="app-top-navigation-drawer"
          sx={{ width: 250 }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            {navItems.map(({ text, to, action, showAvatar }) => (
              <ListItem
                id={`app-top-navigation-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                button
                key={text}
                component={action ? 'button' : Link}
                to={action ? undefined : to}
                onClick={action === 'logout' ? handleLogout : action === 'assistant' ? openAssistant : undefined}
              >
                {showAvatar && profile && (
                  <Avatar
                    src={profile.profile_image_url || '/dope_logo_192.png'}
                    alt="Profile"
                    sx={{
                      width: 28,
                      height: 28,
                      mr: 2,
                      bgcolor: '#0A0F0D',
                    }}
                  />
                )}
                <ListItemText primary={text} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {isAuthenticated && (
        <Box id="app-profile-progress" sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: { xs: 0.5, sm: 1 } }}>
          <IconButton
            id="app-leaderboard-launcher"
            aria-label="Open leaderboard"
            aria-haspopup="dialog"
            aria-expanded={leaderboardOpen}
            aria-controls={leaderboardOpen ? 'friends-leaderboard' : undefined}
            onClick={() => setLeaderboardOpen(true)}
            sx={{
              width: { xs: 40, sm: 52 }, height: { xs: 40, sm: 52 }, flexShrink: 0,
              color: 'primary.main', border: '1px solid', borderColor: 'primary.main', borderRadius: '12px',
              bgcolor: 'background.paper', boxShadow: theme => `2px 3px 0 ${theme.palette.primary.dark}`,
              transition: 'transform 0.15s, box-shadow 0.15s',
              '&:hover': { transform: 'translate(-1px, -1px)' },
              '&:active': { transform: 'translate(1px, 2px)', boxShadow: 'none' },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            <LeaderboardIcon aria-hidden="true" focusable="false" style={{ width: 24, height: 24 }} />
          </IconButton>
          <FriendsLeaderboard open={leaderboardOpen} onClose={() => setLeaderboardOpen(false)} />
          <Box id="app-profile-progress-details" sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'stretch', gap: { xs: 0.75, sm: 1 }, flex: 1, minWidth: 0 }}>
          {profile && <>
          <ArcadeScoreboard
            todayPoints={scores?.today_points}
            highScore={scores?.high_score}
            rewardPoints={profile.last_point_reward}
            rewardThreshold={profile.reward_threshold}
          />
          <AIUsageCredits usage={billingStatus?.ai_usage} source={billingStatus?.entitlement?.source} />
          </>}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default NavBar;
